import { Info } from "lucide-react";
import { useCallback, useEffect, useId, useLayoutEffect, useRef, useState, type ReactNode } from "react";
import { createPortal } from "react-dom";
import { SUMMARY_METRIC_INFO, type SummaryMetricInfoId } from "./summary-metric-info.registry";

export interface SummaryMetricInfoProps {
  metric: SummaryMetricInfoId;
  children?: ReactNode;
  buttonLabel?: string;
}

/** The Cash focus/hover interaction, shared by all definitions and its existing account breakdown. */
export function SummaryMetricInfo({ metric, children, buttonLabel }: SummaryMetricInfoProps) {
  const [open, setOpen] = useState(false);
  const [placement, setPlacement] = useState({ left: 12, top: 12, width: 352, maxHeight: 400 });
  const trigger = useRef<HTMLButtonElement>(null);
  const panel = useRef<HTMLDivElement>(null);
  const closeTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const contentId = useId();
  const definition = SUMMARY_METRIC_INFO[metric];
  const cancelClose = useCallback(() => { if (closeTimer.current !== null) clearTimeout(closeTimer.current); closeTimer.current = null; }, []);
  const show = useCallback(() => { cancelClose(); setOpen(true); }, [cancelClose]);
  const closeAfterPointerLeave = useCallback(() => {
    cancelClose();
    closeTimer.current = setTimeout(() => {
      if (!trigger.current?.contains(document.activeElement) && !panel.current?.contains(document.activeElement)) setOpen(false);
    }, 150);
  }, [cancelClose]);
  const place = useCallback(() => {
    if (!trigger.current) return;
    const bounds = trigger.current.getBoundingClientRect();
    const width = Math.max(1, Math.min(352, window.innerWidth - 24));
    const below = Math.max(0, window.innerHeight - bounds.bottom - 20);
    const above = Math.max(0, bounds.top - 20);
    const naturalHeight = panel.current?.scrollHeight || 200;
    const useAbove = naturalHeight > below && above > below;
    const maxHeight = Math.max(1, Math.min(window.innerHeight - 24, useAbove ? above : below));
    setPlacement({ width, maxHeight,
      left: Math.max(12, Math.min(bounds.left, window.innerWidth - width - 12)),
      top: useAbove ? Math.max(12, bounds.top - Math.min(naturalHeight, maxHeight) - 8) : Math.max(12, Math.min(bounds.bottom + 8, window.innerHeight - maxHeight - 12)),
    });
  }, []);
  useLayoutEffect(() => { if (open) place(); }, [open, place, metric, children]);
  useEffect(() => {
    if (!open) return;
    const outside = (event: PointerEvent) => {
      if (event.target instanceof Node && !trigger.current?.contains(event.target) && !panel.current?.contains(event.target)) {
        cancelClose(); setOpen(false);
      }
    };
    const escape = (event: KeyboardEvent) => {
      if (event.key !== "Escape") return;
      event.stopPropagation(); cancelClose();
      if (panel.current?.contains(document.activeElement)) trigger.current?.focus({ preventScroll: true });
      setOpen(false);
    };
    document.addEventListener("pointerdown", outside);
    document.addEventListener("keydown", escape, true);
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      document.removeEventListener("pointerdown", outside);
      document.removeEventListener("keydown", escape, true);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, cancelClose, place]);
  useEffect(() => cancelClose, [cancelClose]);
  const blur = (relatedTarget: EventTarget | null) => {
    if (!(relatedTarget instanceof Node) || !trigger.current?.contains(relatedTarget) && !panel.current?.contains(relatedTarget)) {
      cancelClose(); setOpen(false);
    }
  };
  return <span className="inline-flex shrink-0" data-metric-info={metric} onMouseEnter={show} onMouseLeave={closeAfterPointerLeave}>
    <button ref={trigger} type="button"
      className="rounded-sm p-0.5 text-muted-foreground hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
      aria-label={buttonLabel ?? `Пояснение: ${definition.label}`}
      aria-describedby={open ? contentId : undefined} aria-controls={open ? contentId : undefined} aria-expanded={open}
      onFocus={show} onClick={show} onBlur={(event) => blur(event.relatedTarget)}>
      <Info className="h-3.5 w-3.5" aria-hidden="true" />
    </button>
    {open && createPortal(<div ref={panel} id={contentId} role="tooltip" tabIndex={0}
      style={{ position: "fixed", ...placement }}
      className="z-[100] overflow-y-auto break-words rounded-md border bg-white p-3 text-xs leading-relaxed text-slate-900 shadow-lg dark:bg-slate-950 dark:text-slate-100"
      onMouseEnter={cancelClose} onMouseLeave={closeAfterPointerLeave} onBlur={(event) => blur(event.relatedTarget)}>
      <p className="mb-2 font-semibold">{definition.label}</p>
      <div className="space-y-2">{definition.paragraphs.map((paragraph) => <p key={paragraph}>{paragraph}</p>)}</div>
      {children && <div className="mt-3 border-t pt-3">{children}</div>}
    </div>, document.body)}
  </span>;
}
